import { StepVehicle } from "./step-vehicle"
import { StepPhotos } from "../quick-estimate/step-photos"
import { StepSides } from "../quick-estimate/step-sides"
import { StepErrorBoundary } from "../quick-estimate/error-boundary"
import type {
  QuickEstimateActions,
  QuickEstimateState,
} from "../quick-estimate/use-quick-estimate-state"
import type { UseQECapture } from "../quick-estimate/use-qe-capture"

// First three steps of every estimate wizard: Vehicle → Photos → Tags.
//
// Both QE3 and full-estimate render the same components with the same
// callbacks at this phase — only the eyebrow labels differ (different
// total step counts) and the Tags "Continue" target (QE3 → Send,
// full-estimate → Processing). Both are passed in as props.
//
// The wizard still owns the AnimatePresence / card chrome around this
// component so it can extend the same animation context to its own
// post-input steps.

export type InputStepIndex = 0 | 1 | 2

interface InputStepsProps {
  step: InputStepIndex
  state: Pick<QuickEstimateState, "vin" | "vehicle" | "photos">
  actions: QuickEstimateActions
  capture: UseQECapture
  /** Called when the user confirms the Tags step. The wizard decides
   *  whether the next stop is Send (QE3) or Processing (FE). */
  onAdvancePastTags: () => void
  /** Optional eyebrow overrides — fall back to per-step defaults
   *  ("Step 1 of 4 · Vehicle", etc.) when omitted. */
  eyebrows?: {
    vehicle?: string
    photos?: string
    tags?: string
  }
}

export function InputSteps({
  step,
  state,
  actions,
  capture,
  onAdvancePastTags,
  eyebrows,
}: InputStepsProps) {
  if (step === 0) {
    return (
      <StepErrorBoundary stepLabel="Vehicle">
        <StepVehicle
          vin={state.vin}
          vehicle={state.vehicle}
          onVin={actions.setVin}
          onVehicle={actions.setVehicle}
          onContinue={() => actions.setStep(1)}
          eyebrowLabel={eyebrows?.vehicle}
        />
      </StepErrorBoundary>
    )
  }

  if (step === 1) {
    return (
      <StepErrorBoundary stepLabel="Photos">
        <StepPhotos
          photos={state.photos}
          actions={actions}
          capture={capture}
          onBack={() => actions.setStep(0)}
          onContinue={() => actions.setStep(2)}
          eyebrowLabel={eyebrows?.photos}
        />
      </StepErrorBoundary>
    )
  }

  return (
    <StepErrorBoundary stepLabel="Tags">
      <StepSides
        photos={state.photos}
        actions={actions}
        capture={capture}
        onBack={() => actions.setStep(1)}
        onContinue={onAdvancePastTags}
        eyebrowLabel={eyebrows?.tags}
      />
    </StepErrorBoundary>
  )
}
